import { useState, useEffect } from 'react';
import { Plus, Save, AlertTriangle, ChevronDown, Image, PenLine, X } from 'lucide-react';
import {
  Package,
  Percent,
  IndianRupee,
  LayoutGrid,
  CalendarDays,
  CalendarCheck,
  Tag,
  Clock,
  Gift,
} from 'lucide-react';

import Sidebar from '../../component/b2c/sidebar/sidebar';
import NavBar from '../../component/b2c/navBar/navBar';
import { useMyContext } from '../../context/MyContext';
import { Base_Url, setOffer } from '../../config/config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export default function OfferSessionPanel() {
  const { isToggleSidebar, isDarkMode } = useMyContext();

  const navigate = useNavigate();

  const [newOffer, setNewOffer] = useState({
    threshold: '',
    description: '',
    discountType: "Percentage",
    category: 'All',
    startDate: '',
    endDate: '',
    image: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [savedMessage, setSavedMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const [activeTab, setActiveTab] = useState('current');

  const categories = ['All', 'shipment cashback'];

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    setNewOffer(prev => ({
      ...prev,
      startDate: today,
      endDate: nextMonth.toISOString().split('T')[0]
    }));
  }, []);

  const validateForm = () => {
    const { threshold, discountType, startDate, endDate, description, image } = newOffer;

    if (!threshold || !discountType || !startDate || !endDate || !image || !description) {
      showMessage("Please fill all required fields", "error");
      return false;
    }

    if (new Date(startDate) > new Date(endDate)) {
      showMessage("End date must be after start date", "error");
      return false;
    }

    return true;
  };

  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const addOffer = async () => {
    if (!validateForm()) return;

    const thresholdValue = parseFloat(newOffer.threshold);
    try {
      const formData = new FormData();
      formData.append('category', newOffer.category);
      formData.append('discount_type', newOffer.discountType);
      formData.append('discount_value', thresholdValue);
      formData.append('description', newOffer.description);

      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      formData.append('start_date', formatDate(newOffer.startDate));
      formData.append('end_date', formatDate(newOffer.endDate));

      if (newOffer.image) {
        formData.append('image', newOffer.image);
      }

      const response = await fetch(Base_Url + setOffer, {
        method: 'POST',
        headers: {
          "cus_id": cus_id,
          "token": token,
        },
        body: formData
      });

      const result = await response.json();
      if (result.status === "SUCCESS") {
        toast.success(result.message);
        navigate('/dashboard')
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Failed to add offer. Please try again.", "error");
    }
  };

  const showMessage = (message, type = 'success') => {
    setSavedMessage(message);
    setMessageType(type);

    setTimeout(() => {
      setSavedMessage('');
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewOffer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  // Compact premium input class
  const inputClass = `w-full text-[11px] border rounded-lg px-2.5 py-1.5 focus:outline-none transition-all duration-300 ${
    isDarkMode
      ? 'border-white/10 bg-white/[0.03] text-white placeholder-gray-500 focus:bg-white/[0.06] focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/20 hover:border-white/20'
      : 'border-gray-300 bg-white text-gray-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
  }`;

  const labelClass = `block text-[9px] font-bold uppercase tracking-[0.15em] mb-1 ${
    isDarkMode ? 'text-gray-500' : 'text-gray-500'
  }`;

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-500 ${
      isDarkMode 
        ? 'dark bg-[#050816]' 
        : 'bg-gray-50'
    }`}>
      {/* Premium background glows */}
      {isDarkMode && (
        <>
          <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/20 blur-[120px]" />
          <div className="pointer-events-none absolute top-1/2 -right-40 h-[400px] w-[400px] rounded-full bg-purple-600/15 blur-[100px]" />
          <div className="pointer-events-none absolute bottom-0 left-1/3 h-[350px] w-[350px] rounded-full bg-pink-600/10 blur-[100px]" />
        </>
      )}
      {!isDarkMode && (
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 blur-3xl" />
      )}

      <NavBar />
      <div className="flex flex-1 overflow-hidden relative z-10">
                           <div
            className={`
              ${isToggleSidebar ? "w-64" : "w-[72px]"}
              m-3
              rounded-3xl
              ${glass}
              overflow-hidden
              transition-all
              duration-300
              ease-in-out
            `}
          >
            <Sidebar />
          </div>
        {/* ✅ overflow-hidden on parent so it doesn't scroll */}
        <div className={`flex-1 overflow-hidden p-3 md:p-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-transparent' : 'bg-gray-200'
        }`}>
          <div className="max-w-5xl mx-auto h-full flex flex-col">

            {/* Compact Premium Header */}
            <div className="mb-3 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className={`relative p-2 rounded-xl border transition-all duration-500 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-rose-500/20 via-pink-500/10 to-transparent border-rose-500/30 shadow-[0_0_20px_rgba(244,63,94,0.2)]' 
                    : 'bg-white border-gray-300'
                }`}>
                  <Gift size={16} className={isDarkMode ? 'text-rose-400 drop-shadow-[0_0_10px_rgba(244,63,94,0.8)]' : 'text-red-500'} />
                </div>
                <div>
                  <h1 className={`text-base font-black tracking-tight transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent' 
                      : 'text-gray-800'
                  }`}>
                    Offer Management
                  </h1>
                  <p className={`text-[10px] mt-0.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Create and manage promotional offers for your customers
                  </p>
                </div>
              </div>
            </div>

            {/* Compact Premium Tabs */}
            <div className={`flex gap-1 mb-3 p-1 rounded-lg w-fit flex-shrink-0 transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-white/[0.03] border border-white/5' 
                : 'bg-white border border-gray-200'
            }`}>
              <button
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all duration-300 ${
                  activeTab === 'current'
                    ? (isDarkMode 
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-[0_0_15px_rgba(99,102,241,0.4)]' 
                        : 'bg-gray-800 text-white shadow-sm')
                    : (isDarkMode 
                        ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' 
                        : 'text-gray-400 hover:text-gray-600')
                }`}
                onClick={() => setActiveTab('current')}
              >
                ✨ Create Offer
              </button>
            </div>

            {activeTab === 'current' && (
              <>
                {/* Compact Premium Offer Form */}
                <div className={`rounded-xl border overflow-hidden flex-1 flex flex-col transition-all duration-500 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-[#0B1120]/80 via-[#0F172A]/80 to-[#0B1120]/80 backdrop-blur-xl border-white/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.5)]' 
                    : 'bg-white border-gray-200 shadow-sm'
                }`}>

                  {/* Compact Premium Form Header */}
                  <div className={`px-4 py-2.5 border-b flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-indigo-500/[0.08] via-purple-500/[0.05] to-transparent border-white/[0.06]' 
                      : 'bg-gray-50 border-gray-100'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className={`p-1 rounded-md transition-colors duration-300 ${
                        isDarkMode 
                          ? 'bg-indigo-500/10 border border-indigo-500/30' 
                          : 'bg-blue-50 border border-gray-300'
                      }`}>
                        <Tag size={11} className={isDarkMode ? 'text-indigo-400' : 'text-blue-600'} />
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${
                        isDarkMode ? 'text-indigo-300' : 'text-gray-600'
                      }`}>
                        New Offer Details
                      </span>
                      {isDarkMode && (
                        <span className="ml-auto flex items-center gap-1.5 text-[9px] text-emerald-400 font-semibold">
                          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                          Ready to create
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Form Body — compact */}
                  <div className="p-4 flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                      {/* Discount Type & Value */}
                      <div className="space-y-1">
                        <label className={labelClass}>Discount *</label>
                        <div className="flex gap-2">
                          <div className="relative w-2/5">
                            <select
                              name="discountType"
                              value={newOffer.discountType}
                              onChange={handleInputChange}
                              className={`${inputClass} appearance-none pr-7 cursor-pointer`}
                            >
                              <option value="Percentage">%</option>
                              <option value="Flat">₹</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none">
                              <ChevronDown size={11} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
                            </div>
                          </div>
                          <div className="relative w-3/5">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                              {newOffer.discountType === "Percentage" ? (
                                <Percent size={11} className={isDarkMode ? 'text-fuchsia-400' : 'text-gray-400'} />
                              ) : (
                                <IndianRupee size={11} className={isDarkMode ? 'text-emerald-400' : 'text-gray-400'} />
                              )}
                            </div>
                            <input
                              type="number"
                              name="threshold"
                              value={newOffer.threshold}
                              onChange={handleInputChange}
                              placeholder="Value"
                              min="0"
                              max={newOffer.discountType === "Percentage" ? "100" : undefined}
                              className={`${inputClass} pl-6`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Category */}
                      <div className="space-y-1">
                        <label className={labelClass}>Category *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <LayoutGrid size={11} className={isDarkMode ? 'text-indigo-400' : 'text-gray-400'} />
                          </div>
                          <select
                            name="category"
                            value={newOffer.category}
                            onChange={handleInputChange}
                            className={`${inputClass} pl-7 pr-7 appearance-none cursor-pointer`}
                          >
                            {categories.filter(category => category !== 'All').map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none">
                            <ChevronDown size={11} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
                          </div>
                        </div>
                      </div>

                      {/* Start Date */}
                      <div className="space-y-1">
                        <label className={labelClass}>Start Date *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <CalendarDays size={11} className={isDarkMode ? 'text-emerald-400' : 'text-gray-400'} />
                          </div>
                          <input
                            type="date"
                            name="startDate"
                            value={newOffer.startDate}
                            onChange={handleInputChange}
                            className={`${inputClass} pl-7`}
                          />
                        </div>
                      </div>

                      {/* End Date */}
                      <div className="space-y-1">
                        <label className={labelClass}>End Date *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <CalendarCheck size={11} className={isDarkMode ? 'text-amber-400' : 'text-gray-400'} />
                          </div>
                          <input
                            type="date"
                            name="endDate"
                            value={newOffer.endDate}
                            onChange={handleInputChange}
                            className={`${inputClass} pl-7`}
                          />
                        </div>
                      </div>

                      {/* Description — single column full width */}
                      <div className="space-y-1 md:col-span-2">
                        <label className={labelClass}>Description *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <PenLine size={11} className={isDarkMode ? 'text-purple-400' : 'text-gray-400'} />
                          </div>
                          <input
                            type="text"
                            name="description"
                            value={newOffer.description}
                            onChange={handleInputChange}
                            placeholder="Enter a short description for this offer"
                            className={`${inputClass} pl-7`}
                          />
                        </div>
                      </div>

                      {/* Image Upload */}
                      <div className="space-y-1 md:col-span-2">
                        <label className={labelClass}>Offer Image *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <Image size={11} className={isDarkMode ? 'text-sky-400' : 'text-gray-400'} />
                          </div>
                          <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={(e) =>
                              setNewOffer({ ...newOffer, image: e.target.files[0] })
                            }
                            className={`w-full text-[11px] border rounded-lg pl-7 pr-2.5 py-1.5 focus:outline-none transition-all duration-300 file:mr-2.5 file:py-1 file:px-2.5 file:rounded-md file:border file:text-[10px] file:font-semibold cursor-pointer ${
                              isDarkMode
                                ? 'border-white/10 bg-white/[0.03] text-white focus:bg-white/[0.06] focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20 file:border-white/10 file:bg-white/[0.05] file:text-gray-300 hover:file:bg-white/[0.1] hover:border-white/20'
                                : 'border-gray-300 bg-white text-gray-800 file:border-gray-300 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200'
                            }`}
                          />
                        </div>
                        {newOffer.image && (
                          <div className="mt-2 relative inline-block group">
                            <div className={`absolute -inset-1 rounded-lg blur opacity-60 group-hover:opacity-100 transition-opacity duration-300 ${
                              isDarkMode ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500' : 'bg-gray-300'
                            }`}></div>
                            <img
                              src={URL.createObjectURL(newOffer.image)}
                              alt="Preview"
                              className={`relative w-14 h-14 object-cover rounded-lg border-2 ${
                                isDarkMode ? 'border-[#0B1120]' : 'border-white'
                              }`}
                            />
                            <button
                              onClick={() => setNewOffer({ ...newOffer, image: '' })}
                              className={`absolute -top-1.5 -right-1.5 p-0.5 rounded-full text-white transition-all duration-300 shadow-lg z-10 ${
                                isDarkMode 
                                  ? 'bg-gradient-to-r from-rose-500 to-red-500 hover:shadow-[0_0_15px_rgba(244,63,94,0.6)] hover:scale-110' 
                                  : 'bg-gray-800 hover:bg-gray-700'
                              }`}
                            >
                              <X size={10} />
                            </button>
                          </div>
                        )}
                      </div>

                    </div>

                    {/* Compact Premium Form Footer */}
                    <div className={`mt-4 pt-3 border-t flex justify-end transition-colors duration-300 ${
                      isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'
                    }`}>
                      <button
                        onClick={addOffer}
                        className={`group relative px-5 py-2 text-[11px] font-bold rounded-lg transition-all duration-300 flex items-center gap-2 overflow-hidden ${
                          isDarkMode
                            ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-[0_0_25px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.7)] hover:scale-[1.03]'
                            : 'bg-red-600 text-white hover:opacity-80 shadow-sm'
                        }`}
                      >
                        {isDarkMode && (
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                        )}
                        <Plus size={12} className="relative z-10" />
                        <span className="relative z-10">Create Offer</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Premium Notification Toast */}
            {savedMessage && (
              <div className={`fixed bottom-6 right-6 px-4 py-2.5 text-[11px] font-semibold rounded-xl flex items-center gap-2 backdrop-blur-xl border transition-all duration-300 ${
                messageType === 'success'
                  ? (isDarkMode
                      ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.3)]'
                      : 'bg-gray-800 text-white border-gray-700 shadow-lg')
                  : (isDarkMode
                      ? 'bg-rose-500/10 text-rose-300 border-rose-500/30 shadow-[0_0_30px_rgba(244,63,94,0.3)]'
                      : 'bg-white text-gray-800 border-gray-300 shadow-lg')
              }`}>
                {messageType === 'success' ? (
                  <Save size={13} />
                ) : (
                  <AlertTriangle size={13} />
                )}
                {savedMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}